import { useRef, useState } from "react";
import PhoneInput, { isValidPhoneNumber } from "react-phone-number-input";
import "react-phone-number-input/style.css";
import { Paperclip, X } from "lucide-react";
import { api, errorText } from "../lib/api";
import { CountrySelect } from "./CountrySelect";
import { Checkbox } from "./ui/checkbox";

const OPTS = {
  industry: ["Makeup", "Skincare", "Beauty", "Jewellery", "Other"],
  service: ["Image Advertisements", "Video Advertisements", "Image + Video", "Monthly Collaboration", "Not Sure Yet"],
  project_type: ["One-Time Project", "Product Launch", "Campaign", "Long-Term Collaboration"],
  budget: ["Under ₹25,000", "₹25,000 – ₹50,000", "₹50,000 – ₹1,00,000", "Above ₹1,00,000", "Prefer to Discuss"],
  timeline: ["Within 2 Weeks", "Within 1 Month", "Flexible", "Just Exploring"],
};
const REQUIRED = ["full_name", "brand_name", "email", "country", "industry", "service", "project_type", "description"];
const EMPTY = { full_name: "", brand_name: "", email: "", phone: "", country: "", website: "", industry: "", service: "", project_type: "", budget: "", description: "", timeline: "" };
const FILE_TYPES = ["application/pdf", "image/jpeg", "image/png", "image/webp"];

const Field = ({ label, name, required, error, children, className = "" }) => (
  <div className={className}>
    <label htmlFor={`q-${name}`} className="field-label">{label}{required && <span className="text-wine"> *</span>}</label>
    {children}
    {error && <p className="field-error" data-testid={`quote-error-${name}`}>{error}</p>}
  </div>
);

export const QuoteForm = ({ onSuccess }) => {
  const [v, setV] = useState(EMPTY);
  const [file, setFile] = useState(null);
  const [consent, setConsent] = useState(false);
  const [errors, setErrors] = useState({});
  const [sending, setSending] = useState(false);
  const [formError, setFormError] = useState("");
  const startedAt = useRef(Date.now());
  const fileRef = useRef(null);
  const hp = useRef(null);

  const set = (k) => (e) => {
    setV((s) => ({ ...s, [k]: e?.target ? e.target.value : e || "" }));
    setErrors((s) => (s[k] ? { ...s, [k]: undefined } : s));
  };

  const pickFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!FILE_TYPES.includes(f.type)) return setErrors((s) => ({ ...s, brief: "Please upload a PDF, JPG, PNG or WEBP file." }));
    if (f.size > 10 * 1024 * 1024) return setErrors((s) => ({ ...s, brief: "File must be 10 MB or smaller." }));
    setErrors((s) => ({ ...s, brief: undefined }));
    setFile(f);
  };

  const validate = () => {
    const err = {};
    REQUIRED.forEach((k) => { if (!v[k].trim()) err[k] = "This field is required."; });
    if (v.email && !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(v.email)) err.email = "Please enter a valid email address.";
    if (v.phone && !isValidPhoneNumber(v.phone)) err.phone = "Please enter a valid phone number.";
    if (!consent) err.consent = "Please confirm to continue.";
    setErrors(err);
    return !Object.keys(err).length;
  };

  const submit = async (e) => {
    e.preventDefault();
    setFormError("");
    if (!validate()) return;
    setSending(true);
    const fd = new FormData();
    Object.entries(v).forEach(([k, val]) => fd.append(k, val));
    fd.append("consent", "true");
    fd.append("hp_note", hp.current?.value || "");
    fd.append("started_at", String(startedAt.current));
    if (file) fd.append("brief", file);
    try {
      await api.post("/enquiries", fd, { withCredentials: false });
      onSuccess();
    } catch (err) {
      setFormError(errorText(err));
    } finally {
      setSending(false);
    }
  };

  const select = (name, label, required, placeholder = "Select an option") => (
    <Field label={label} name={name} required={required} error={errors[name]}>
      <select id={`q-${name}`} value={v[name]} onChange={set(name)} className="field" data-testid={`quote-${name}-select`}>
        <option value="">{placeholder}</option>
        {OPTS[name].map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    </Field>
  );
  const input = (name, label, required, props = {}) => (
    <Field label={label} name={name} required={required} error={errors[name]}>
      <input id={`q-${name}`} value={v[name]} onChange={set(name)} className="field" data-testid={`quote-${name}-input`} {...props} />
    </Field>
  );

  return (
    <form onSubmit={submit} noValidate data-testid="quote-request-form" className="grid gap-x-10 gap-y-10 md:grid-cols-2">
      <div aria-hidden className="absolute -left-[9999px] h-0 w-0 overflow-hidden">
        <label>Leave this empty<textarea ref={hp} name="hp_note" tabIndex={-1} autoComplete="off" /></label>
      </div>
      {input("full_name", "Full Name", true, { autoComplete: "name", placeholder: "Your name" })}
      {input("brand_name", "Brand / Company Name", true, { autoComplete: "organization", placeholder: "Your brand" })}
      {input("email", "Email Address", true, { type: "email", autoComplete: "email", placeholder: "name@brand.com" })}
      <Field label="Phone / WhatsApp Number" name="phone" error={errors.phone}>
        <PhoneInput id="q-phone" international defaultCountry="IN" value={v.phone} onChange={set("phone")} data-testid="quote-phone-input" />
      </Field>
      <Field label="Country" name="country" required error={errors.country}>
        <CountrySelect id="q-country" value={v.country} onChange={set("country")} />
      </Field>
      {input("website", "Brand Website / Instagram", false, { placeholder: "www.yourbrand.com or @yourbrand" })}
      {select("industry", "Industry", true)}
      {select("service", "Service Required", true)}
      {select("project_type", "Project Type", true)}
      {select("budget", "Approximate Budget", false)}
      <Field label="Project Description" name="description" required error={errors.description} className="md:col-span-2">
        <textarea id="q-description" rows={5} value={v.description} onChange={set("description")} maxLength={5000} className="field resize-none" placeholder="Your product, collection or campaign — and the feeling you'd like it to have." data-testid="quote-description-input" />
      </Field>
      {select("timeline", "Expected Timeline", false)}
      <Field label="Upload Brand Brief / References" name="brief" error={errors.brief}>
        <input ref={fileRef} id="q-brief" type="file" accept=".pdf,.jpg,.jpeg,.png,.webp" onChange={pickFile} className="hidden" data-testid="quote-brief-input" />
        {file ? (
          <div className="flex items-center justify-between border-b border-line py-3 text-sm">
            <span className="truncate pr-4" data-testid="quote-brief-name">{file.name}</span>
            <button type="button" onClick={() => { setFile(null); fileRef.current.value = ""; }} aria-label="Remove file" data-testid="quote-brief-remove"><X size={16} className="text-wine" /></button>
          </div>
        ) : (
          <button type="button" onClick={() => fileRef.current?.click()} className="field flex items-center gap-3 text-left text-muted-ink" data-testid="quote-brief-button">
            <Paperclip size={15} className="text-wine" /> Optional — PDF, JPG, PNG or WEBP, up to 10 MB
          </button>
        )}
      </Field>
      <div className="md:col-span-2">
        <label className="flex cursor-pointer items-start gap-4 text-sm leading-relaxed text-charcoal/85">
          <Checkbox checked={consent} onCheckedChange={(c) => { setConsent(!!c); setErrors((s) => ({ ...s, consent: undefined })); }} className="mt-0.5 border-wine data-[state=checked]:bg-wine" data-testid="quote-consent-checkbox" />
          <span>I agree to BWI storing my contact information to respond to this enquiry, as described in the <a href="/privacy" className="text-wine underline">Privacy Policy</a>. <span className="text-wine">*</span></span>
        </label>
        {errors.consent && <p className="field-error" data-testid="quote-error-consent">{errors.consent}</p>}
      </div>
      <div className="md:col-span-2">
        {formError && <p className="mb-6 border-l-2 border-wine bg-white px-4 py-3 text-sm text-wine" data-testid="quote-form-error">{formError}</p>}
        <button type="submit" disabled={sending} className="btn btn-primary w-full justify-center disabled:opacity-60 md:w-auto" data-testid="quote-form-submit-btn">
          {sending ? "Sending…" : "Send My Enquiry"}
        </button>
      </div>
    </form>
  );
};
